import {
  EnrichedOntologyFieldDefinition,
  useHistogramQueries,
} from 'features/dynamicOntology'
import { useRolodexHistogram } from 'features/rolodex'
import { useMemo } from 'react'
import { RolodexHistogramFilter, RolodexHistogramQuery } from 'types'

import { isPairableSecondaryField } from './valueListFilterPanelData'

const EMPTY_SECONDARY_VALUES: Readonly<Record<string, string>> = {}

/**
 * Pairs each value of `matchFieldDef` with the same entry's `secondaryFieldDef`,
 * so a disambiguation dropdown can show more than a bare value. Fields that
 * can't be paired yield an empty map and cost no query.
 */
export function useSecondaryValues(
  matchFieldDef: EnrichedOntologyFieldDefinition | undefined,
  secondaryFieldDef: EnrichedOntologyFieldDefinition | undefined,
  filters: ReadonlyArray<RolodexHistogramFilter>
): Readonly<Record<string, string>> {
  const select = useMemo<RolodexHistogramQuery['select']>(() => {
    if (
      !matchFieldDef ||
      !secondaryFieldDef ||
      !isPairableSecondaryField(matchFieldDef, secondaryFieldDef)
    ) {
      return []
    }
    return [
      {
        type: matchFieldDef.entryType,
        [matchFieldDef.keyOrField]: matchFieldDef.key,
        label: 'value',
        group_by: true,
      },
      {
        type: secondaryFieldDef.entryType,
        [secondaryFieldDef.keyOrField]: secondaryFieldDef.key,
        label: 'secondary',
        group_by: true,
      },
    ]
  }, [matchFieldDef, secondaryFieldDef])

  const {
    queries: [query],
  } = useHistogramQueries({
    select,
    filters,
    queryType: matchFieldDef?.entryType,
  })
  const { data } = useRolodexHistogram(select.length ? query : undefined)

  return useMemo(() => {
    if (!data) {
      return EMPTY_SECONDARY_VALUES
    }
    const secondaryValuesByValue: Record<string, string> = {}
    data.flat().forEach((entry) => {
      const value = String(entry['value'] ?? '')
      const secondary = entry['secondary']
      // Values arrive untyped, and one value can repeat across entries with
      // differing secondary values, so the first non-empty one wins.
      if (value && typeof secondary === 'string' && secondary) {
        secondaryValuesByValue[value] ??= secondary
      }
    })
    return secondaryValuesByValue
  }, [data])
}
